import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { costCenterService } from '../api/apiService';
import { constcenter1 as fileList1, constcenter2 as fileList2 } from '../helpers/costCenters';
import { useAuth } from './AuthContext';

/**
 * Cost centers for the whole app, loaded once from the API.
 *
 * `constcenter1` (Kostenstellen) and `constcenter2` (Kostenträger) keep the exact shape the
 * components have always consumed from src/helpers/costCenters.js, so the swap from the file to
 * the database is invisible to them: `{ id, value, label, cnAccountNumber, accountNumber,
 * lanoAccountNumber, lanoId, type, isActive }`.
 *
 * Until the API has answered — or if it fails (old backend, network) — the lists fall back to
 * the file, so Propose Talent is never blocked by the settings feature. Only ACTIVE entries are
 * exposed here; the Settings page asks the API for inactive ones itself.
 */
const CostCenterContext = createContext(null);

const byLabel = (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.label).localeCompare(String(b.label));

export function CostCenterProvider({ children }) {
  // Re-fetch whenever the logged-in user changes: the provider mounts before login, and the
  // request interceptor needs the token that login stores.
  const { currentUser } = useAuth();
  const [lists, setLists] = useState({ constcenter1: fileList1, constcenter2: fileList2 });
  const [source, setSource] = useState('file'); // 'file' | 'api'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inflight = useRef(null);

  const refresh = useCallback(async () => {
    if (inflight.current) return inflight.current;
    setLoading(true);
    inflight.current = costCenterService.getAll()
      .then((res) => {
        const all = res.data?.costCenters || [];
        const active = all.filter((c) => c.isActive !== false);
        setLists({
          constcenter1: active.filter((c) => Number(c.type) === 1).sort(byLabel),
          constcenter2: active.filter((c) => Number(c.type) === 2).sort(byLabel),
        });
        setSource('api');
        setError(null);
      })
      .catch((err) => {
        console.warn('[cost centers] API unavailable, using the built-in list:', err?.message || err);
        setError(err);
        setSource('file');
      })
      .finally(() => {
        setLoading(false);
        inflight.current = null;
      });
    return inflight.current;
  }, []);

  useEffect(() => {
    // Only load once someone is logged in; the request interceptor needs the token.
    if (localStorage.getItem('token')) refresh();
    else setLoading(false);
  }, [refresh, currentUser]);

  const value = useMemo(() => ({
    constcenter1: lists.constcenter1,
    constcenter2: lists.constcenter2,
    loading,
    error,
    source,
    refresh,
  }), [lists, loading, error, source, refresh]);

  return <CostCenterContext.Provider value={value}>{children}</CostCenterContext.Provider>;
}

/**
 * Drop-in replacement for `import { constcenter1, constcenter2 } from '../../helpers/costCenters'`.
 * Works without a provider too (tests, isolated renders) — then it returns the file lists.
 */
export function useCostCenters() {
  const ctx = useContext(CostCenterContext);
  if (!ctx) {
    return { constcenter1: fileList1, constcenter2: fileList2, loading: false, error: null, source: 'file', refresh: async () => {} };
  }
  return ctx;
}

export default CostCenterContext;
